import { useState, useCallback, useRef, useEffect } from 'react';
import { shippingApiService } from '@/services/shippingApiService';
import { useErrorHandler } from '@/hooks/useErrorHandler';

/**
 * Hook para calcular y cachear costos de envío
 *
 * Maneja el cálculo de costos de envío con cache automático para evitar
 * recalcular con los mismos parámetros. Útil para mostrar costos en
 * detalle de productos, carrito, y checkout.
 *
 * **Features:**
 * - Cache automático (evita recalcular si params no cambian)
 * - Loading states durante cálculo
 * - Error handling integrado con useErrorHandler
 * - Cleanup de cache al desmontar
 *
 * **Casos de uso:**
 * - Mostrar costo de envío en detalle de artwork
 * - Calcular costo total en carrito
 * - Preview de costos en checkout
 *
 * @module hooks/useShippingCost
 *
 * @returns {Object} Estado y métodos del hook
 * @returns {Object|null} return.cost_data - Datos del costo calculado
 * @returns {number} return.cost_data.cost - Costo en CLP
 * @returns {string} return.cost_data.estimated_days - Tiempo estimado (ej: "3-5 días")
 * @returns {boolean} return.cost_data.available - Si el envío está disponible
 * @returns {string} return.cost_data.service_type - Tipo de servicio
 * @returns {string} return.cost_data.currency - Moneda (CLP)
 * @returns {boolean} return.cost_data.cached - Si proviene del cache
 * @returns {boolean} return.loading - Si está calculando
 * @returns {Object} return.error_state - Estado de error (de useErrorHandler)
 * @returns {Function} return.calculateCost - Función para calcular costo
 * @returns {Function} return.clearCost - Limpiar datos de costo
 * @returns {Function} return.clearError - Limpiar error
 *
 * @example
 * import { useShippingCost } from '@/hooks/useShippingCost';
 *
 * function ArtworkDetail({ artwork_id }) {
 *   const {
 *     cost_data,
 *     loading,
 *     error_state,
 *     calculateCost,
 *     clearError
 *   } = useShippingCost();
 *
 *   const handleCalculate = async () => {
 *     await calculateCost({
 *       artwork_id,
 *       destination_county_code: 'STGO',
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleCalculate}>Calcular envío</button>
 *       {loading && <LoadingSpinner />}
 *       {cost_data && <p>Costo: ${cost_data.cost}</p>}
 *       {error_state.has_error && <ErrorMessage message={error_state.error_message} />}
 *     </div>
 *   );
 * }
 */
export function useShippingCost() {
  const [cost_data, setCostData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Error handling
  const { error_state, setError, clearError } = useErrorHandler();

  // Cache de última solicitud para evitar recalcular con mismos params
  // { artwork_id, destination_county_code, shipping_type }
  const last_params_ref = useRef(null);

  /**
   * ✅ Ref para cost_data para evitar dependencia en useCallback
   * Esto previene re-creación de calculateCost cada vez que cost_data cambia
   */
  const cost_data_ref = useRef(null);

  /**
   * Verificar si los parámetros son iguales a la última solicitud
   * @param {Object} params - Parámetros a comparar
   * @returns {boolean} true si son iguales
   */
  const areParamsEqual = useCallback((params) => {
    if (!last_params_ref.current) return false;

    const prev = last_params_ref.current;
    return (
      prev.artwork_id === params.artwork_id &&
      prev.destination_county_code === params.destination_county_code &&
      prev.shipping_type === params.shipping_type
    );
  }, []);

  /**
   * Calcular costo de envío
   *
   * Si los parámetros son iguales a la última solicitud exitosa,
   * retorna el resultado cacheado sin hacer nueva llamada al API.
   *
   * @param {Object} params - Parámetros para calcular envío
   * @param {string} params.artwork_id - UUID del artwork (required)
   * @param {string} params.destination_county_code - Código de comuna Chilexpress (required, ej: "STGO")
   * @param {string} [params.shipping_type='standard'] - Tipo de envío: 'standard' | 'express'
   * @returns {Promise<boolean>} true si éxito, false si error
   *
   * @example
   * // Calcular envío standard
   * await calculateCost({
   *   artwork_id: '550e8400-e29b-41d4-a716-446655440001',
   *   destination_county_code: 'STGO',
   *   shipping_type: 'standard'
   * });
   *
   * @example
   * // Calcular envío express
   * await calculateCost({
   *   artwork_id: '550e8400-e29b-41d4-a716-446655440001',
   *   destination_county_code: 'VALP',
   *   shipping_type: 'express'
   * });
   */
  const calculateCost = useCallback(
    async (params) => {
      // Validar parámetros requeridos
      if (!params.artwork_id || !params.destination_county_code) {
        setError('Faltan parámetros para calcular envío');
        return false;
      }

      // Cache: si params son iguales a la última solicitud exitosa, no recalcular
      if (areParamsEqual(params) && cost_data_ref.current) {
        // Marcar como cached para debugging
        setCostData({ ...cost_data_ref.current, cached: true });
        return true;
      }

      try {
        setLoading(true);
        clearError();

        const { success, data, error } = await shippingApiService.calculateShippingCost({
          artwork_id: params.artwork_id,
          destination_county_code: params.destination_county_code,
          shipping_type: params.shipping_type || 'standard',
        });

        if (!success || error) {
          setError(error || 'No se pudo calcular el costo de envío');
          setCostData(null);
          cost_data_ref.current = null; // ✅ Limpiar ref
          last_params_ref.current = null;
          return false;
        }

        // Guardar resultado
        setCostData(data);
        cost_data_ref.current = data; // ✅ Actualizar ref
        last_params_ref.current = params;
        return true;
      } catch (error) {
        setError(error);
        setCostData(null);
        cost_data_ref.current = null; // ✅ Limpiar ref
        last_params_ref.current = null;
        return false;
      } finally {
        setLoading(false);
      }
    },
    [areParamsEqual, setError, clearError] // ✅ Removido cost_data para prevenir re-creación
  );

  /**
   * Limpiar datos de costo y cache
   */
  const clearCost = useCallback(() => {
    setCostData(null);
    cost_data_ref.current = null; // ✅ Limpiar ref
    last_params_ref.current = null;
    clearError();
  }, [clearError]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      last_params_ref.current = null;
    };
  }, []);

  return {
    cost_data,
    loading,
    error_state,
    calculateCost,
    clearCost,
    clearError,
  };
}
